/**
 * Compact relative-date formatting for the Product Dev list.
 *
 * The list is a worklist, not an archive: absolute locale dates read as noise
 * (see DESIGN.md §17 — dates go through a single formatter, never inline
 * literals). We surface a short relative label ("in 5d", "3w ago", "today")
 * for scanning and keep the absolute date in a `title` tooltip for precision.
 */
import { differenceInCalendarDays, format, parseISO } from 'date-fns';

export interface RelativeDate {
  /** Compact relative label, e.g. "in 5d", "3w ago", "today". */
  short: string;
  /** Absolute date for the `title` tooltip, e.g. "5 Jul 2026". */
  absolute: string;
  /** True when the date falls strictly before today (calendar days). */
  isPast: boolean;
}

/** Collapse a day count into the largest sensible compact unit. */
const compactMagnitude = (absDays: number): string => {
  if (absDays === 0) return 'today';
  if (absDays < 7) return `${absDays}d`;
  if (absDays < 30) return `${Math.round(absDays / 7)}w`;
  if (absDays < 365) return `${Math.round(absDays / 30)}mo`;
  return `${Math.round(absDays / 365)}y`;
};

export const toRelativeDate = (iso: string, now: Date = new Date()): RelativeDate => {
  const date = parseISO(iso);
  const days = differenceInCalendarDays(date, now); // future positive, past negative
  const absDays = Math.abs(days);
  const magnitude = compactMagnitude(absDays);
  const short = days === 0 ? 'today' : days > 0 ? `in ${magnitude}` : `${magnitude} ago`;
  return {
    short,
    absolute: format(date, 'd MMM yyyy'),
    isPast: days < 0,
  };
};

/**
 * Statuses where a past expected date is NOT "overdue" — the work is finished
 * or parked, so a past due date is expected, not a warning. Single source of
 * truth for the list group summary AND the detail deadline widget so they
 * cannot drift.
 */
export const OVERDUE_EXEMPT_STATUSES: ReadonlySet<string> = new Set([
  'approved',
  'rejected',
  'on_hold',
]);

/** True when `expected` is strictly past and the status isn't overdue-exempt. */
export const isOverdue = (
  expected: string | null | undefined,
  status: string,
  now: Date = new Date(),
): boolean =>
  Boolean(expected) &&
  toRelativeDate(expected as string, now).isPast &&
  !OVERDUE_EXEMPT_STATUSES.has(status);
